/**
 * The shell: title screen, the setup flow, and the battle-mode setup board.
 *
 * Rendered as a full-screen DOM layer over the canvas so it can use real type
 * and real layout — the canvas is for the field, not for menus.
 *
 * ── The setup flow, and why it is shaped the way it is ──────────────────────
 *
 * The owner's phrasing was "the player should have a choice of the map then
 * decide which kingdom to play, which decided the race and the character — so
 * assign a character to each kingdom as leader". Every clause of that is load
 * bearing, and the flow is a direct reading of it:
 *
 *   1. **The Realm.** The board, and the difficulty of holding a corner of it.
 *      One realm today; see the note on that step for what `worldgen` has to
 *      expose before it can become a browser of several.
 *   2. **The Kingdom.** Twenty-two of them, two houses on each of eleven
 *      peoples. Choosing one fixes the people, the leader, the muster binding,
 *      the economy, the Academy's fourth column and the hero class the leader
 *      fights as — all of it, in one decision.
 *
 * There is no longer a **Character** step, and its removal is the point rather
 * than a simplification. A character was a third sibling choice next to faction
 * and difficulty; the owner's direction makes it a *consequence*. So the leader
 * is the face of the kingdom screen, and `heroId` is read off the leader's
 * `archetype` rather than asked for — the existing hero system is inherited
 * whole and nothing about it changed.
 *
 * ── Why one command and not two ─────────────────────────────────────────────
 *
 * `Cmd.PickKingdom` takes a kingdom id — which resolves through the leader
 * table, because a kingdom *is* a house on a race. The race and the leader are
 * consequences, so there is exactly one thing to send. Two commands would let
 * this screen offer a combination the world does not contain.
 *
 * It is refused once `tick > 0`, so the choice must be in the world's config
 * before `initRun` folds the identity into `w.mods`. That is why the choice
 * travels as `raceId`/`kingdomId` on `CampaignChoice` rather than as a queued
 * command: `main.ts` puts them straight into the `WorldConfig`, which is the
 * door `races` built for exactly this.
 *
 * ── What is deliberately not here ───────────────────────────────────────────
 *
 * The styles are injected by this file rather than appended to `src/ui/style.ts`.
 * That file is shared and cannot be split; three agents appending to it merges
 * cleanly and two editing one rule does not, and this screen needs no rule any
 * other screen wants. Nothing here touches it, which is the cheapest possible
 * merge.
 */

import { PLAYABLE_FACTIONS, FACTIONS, faction } from '../data/factions.ts';
import { PROVINCES, PROVINCE_COUNT } from '../data/provinces.ts';
import { NATIONS } from '../data/nations.ts';
import { REGIONS, SEA_LANES } from '../data/realm.ts';
import { leaderPortrait } from './leaderportrait.ts';
import { hydrateLeaderArt } from './artdrop.ts';
import { housesDiffer } from './racefaces.ts';
import { kingdom, kingdomSheet, kinOrder, kingdomsOfKin, railLine, siblingHouses, type KingdomSheet } from './kingdoms.ts';
import { SCENARIOS, type ScenarioKind } from '../sim/battle.ts';
import { didMigrate, listSaves, storageStatus, type SaveSummary } from './storage.ts';
import { bloodline, unlockRows, type UnlockRow } from './bloodline.ts';
import { BLOODLINE_BRANCHES } from '../data/bloodline.ts';
import { CODEX_TABS, codexFirst, codexRail, codexSheet, type CodexTab } from './codex.ts';
import {
  listRooms,
  listSaves as listRoomSaves,
  relayUrl,
  type RoomInfo,
  type SavedRoom,
} from '../net/session.ts';
import { KEY_HELP } from './keys.ts';
import { tutorialMenuNote, tutorialProgress } from './tutorial.ts';
import { seedToCode } from '../core/rng.ts';
import { toHex } from '../data/palette.ts';

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}

function ago(ts: number): string {
  const mins = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export interface CampaignChoice {
  factionId: string;
  difficultyId: string;
  /** A `HeroClass` id, or '' to play the old way, with no character. */
  heroId: string;
  seed: number;
  /**
   * The people, and the house that leads them. Both optional so every existing
   * call site — the battle board, the URL launcher, the shot harness — keeps
   * working and gets exactly the race it always implicitly had.
   *
   * `kingdomId` is a `Leader` id and is the payload `Cmd.PickKingdom` takes.
   */
  raceId?: string;
  kingdomId?: string;
}

export interface BattleChoice {
  scenario: ScenarioKind;
  factionId: string;
  enemyFactionId: string;
  points: number;
  difficultyId: string;
  seed: number;
  /**
   * The second banner a side, or null for the 1v1 this game has always been.
   *
   * Both or neither. A 2v2 with one crown short is a 2v1, which the simulation
   * will field quite happily and which nobody chose from this screen — so the
   * two are set and cleared together, and the toggle is what does it.
   */
  allyFactionId: string | null;
  enemyAllyFactionId: string | null;
}

export interface MenuCallbacks {
  /**
   * The game: a realm of provinces and dozens of powers, and one city in it
   * that is yours to build. Everything else on this screen is a way of
   * practising part of it in isolation.
   */
  onCampaign(c: CampaignChoice): void;
  /** The settlement and its sieges, with no realm around it. */
  onSiegeOnly(c: CampaignChoice): void;
  /** Sound, from the title screen — the same door the in-game panel uses. */
  onAudioSettings?(patch: { master?: number; effects?: number; ambience?: number; muted?: boolean }): void;
  audioSettings?(): { master: number; effects: number; ambience: number; muted: boolean };
  /**
   * The same, held by a room of players. `host` carries the world recipe;
   * everyone else inherits it through the relay and only brings a name.
   */
  onCoop(o: {
    server: string;
    room: string;
    roomName: string;
    nickname: string;
    host: boolean;
    players: number;
    resume: boolean;
    choice?: CampaignChoice;
  }): void;
  /** One field, two armies, no economy. */
  onBattle(c: BattleChoice): void;
  /**
   * Resume a saved run. The slot is explicit because there is more than one now
   * — see `listSaves` — and "the newest" is only the default, not the rule.
   */
  onContinue(slot: number): void;
  /** A guided first run: Hold the Hearth on Ember with the lesson cards up. */
  onTutorial(): void;
}

/** "turn 41" for a campaign, "wave 12" for a settlement held on its own. */
function savePosition(s: SaveSummary): string {
  return s.campaign ? `turn ${s.turn}` : `wave ${s.wave}`;
}

/**
 * One row of a title-screen list.
 *
 * The row itself is a word. Everything the old title screen said inside its
 * cards — what the mode is, what it costs you, how far the last run got —
 * lives in `hint`, which the foot of the screen prints one at a time for
 * whichever row the player is on. That is the whole of the redesign: the
 * prose did not go away, it stopped being read six paragraphs at once.
 */
interface MenuItem {
  /** The word on the row. One or two; this is a list, not a paragraph. */
  label: string;
  /** The sentence the hint line shows while this row has the focus. */
  hint: string;
  /** A screen to open, or a verb `renderList`'s caller recognises. */
  go: string;
  /** A quieter second line. Only the save rows need one — they carry a date. */
  note?: string;
  /** Which slot to resume, for the rows that resume one. */
  slot?: number;
}

const ARMY_SIZES: Array<{ label: string; points: number; note: string }> = [
  { label: 'Skirmish', points: 700, note: 'about six companies a side' },
  { label: 'Battle', points: 1600, note: 'about ten companies a side' },
  { label: 'Grand Battle', points: 3200, note: 'as many as the field will hold' },
];

/**
 * The two sizes the portraits are drawn at, and the only two.
 *
 * 46 rather than a rounder number because that is the size `portraits` builds
 * and tests the thumbnails at, and a screen shipping 28 while the art is
 * verified at 46 means the art was verified against nothing. The hero shot is
 * larger than the 190 they sheet at, which is the safe direction: the failures
 * at this scale are all at the small end.
 */
const THUMB = 46;
const HERO_SHOT = 248;

type Screen =
  | 'title'
  | 'newgame'
  | 'load'
  | 'credits'
  | 'settings'
  | 'realm'
  | 'kingdom'
  | 'battle'
  | 'siege-kingdom'
  | 'bloodline'
  | 'codex'
  | 'coop'
  | 'coop-kingdom';

export class Menu {
  private el: HTMLElement;
  private visible = true;

  private factionId = 'ashen';
  private enemyFactionId = 'ironfang';
  private difficultyId = 'endless';
  /**
   * What the co-op screen has been told so far, kept between the lobby and
   * the kingdom picker the host visits next. The nickname outlives the
   * session in `localStorage` — nobody wants to retype it every evening.
   */
  /** Whether the kingdom being picked is for a co-op room rather than a solo run. */
  private kingdomForCoop = false;
  /** True while the kingdom picker is choosing a crown for a room we join. */
  private coopJoining = false;
  /**
   * True while the room being opened is a *reopened* one.
   *
   * It travels as far as `startCoop`, which uses it for nothing but honesty —
   * the server decides whether a room resumes, by whether it has a save under
   * that code — and it is what stops the kingdom screen's fresh seed from being
   * mistaken for a new realm. The recipe of a room is spoken once; see the
   * `config` handler in `server/hearth.ts`.
   */
  private coopResume = false;
  private coopDraft = {
    room: '',
    roomName: '',
    nickname: '',
    players: 2,
  };
  /** A `Leader` id. The one thing the kingdom screen decides. */
  private kingdomId = 'ashen_vane';
  private scenario: ScenarioKind = 'open_field';
  private points = 1600;
  /** Null while the field is a 1v1. See `BattleChoice`. */
  private allyFactionId: string | null = null;
  private enemyAllyFactionId: string | null = null;
  private seed = Date.now() & 0x7fffffff;

  /** What Escape does on the current list screen. Unset on the title itself. */
  private escapeTo: Screen | null = null;

  constructor(private cb: MenuCallbacks) {
    this.el = document.createElement('div');
    this.el.id = 'menu';
    (document.getElementById('ui-root') as HTMLElement).appendChild(this.el);
    this.injectStyle();
    // On the menu's own element rather than on `window`, so it runs before
    // `main.ts`'s global keydown and can stop the event reaching it. Every key
    // this screen uses — Space especially, which sounds the horn — means
    // something else in the game.
    this.el.addEventListener('keydown', (e) => this.onKey(e));
    this.render('title');
  }

  /**
   * Arrows, Enter and Escape on a list screen.
   *
   * Enter and Space are handled here rather than left to the button's native
   * activation, because native activation does not stop the keypress: Space
   * would select the row *and* sound the horn behind the menu.
   */
  private onKey(e: KeyboardEvent): void {
    const items = [...this.el.querySelectorAll<HTMLButtonElement>('.tt-item')];
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const focus = (i: number): void => {
      items[((i % items.length) + items.length) % items.length].focus();
    };
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        focus(at < 0 ? 0 : at + (e.key === 'ArrowDown' ? 1 : -1));
        break;
      case 'Home':
        focus(0);
        break;
      case 'End':
        focus(items.length - 1);
        break;
      case 'Enter':
      case ' ':
        if (at >= 0) items[at].click();
        break;
      case 'Escape':
        if (this.escapeTo === null) return;
        this.render(this.escapeTo);
        break;
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation();
  }

  private injectStyle(): void {
    const s = document.createElement('style');
    s.textContent = `
      /* flex-start plus an auto margin on the child, rather than the obvious
         align-items:center — and the difference only shows on a screen taller
         than the window. Centring a flex child that overflows puts its top
         *above* the scroll origin, where no scrollbar can reach it; the
         Options screen grew a keyboard section and lost its own heading that
         way. An auto margin resolves to zero when there is no free space, so
         this centres what fits and top-aligns what does not. */
      #menu { position: absolute; inset: 0; z-index: 90; pointer-events: auto;
        background: radial-gradient(ellipse at 50% 38%, #24201b 0%, #0f0e0c 62%, #080807 100%);
        display: flex; align-items: flex-start; justify-content: center; overflow-y: auto; }
      #menu.gone { display: none; }
      #menu .wrap { width: min(1080px, 94vw); padding: 40px 0; margin: auto; }
      #menu .wrap.wide { width: min(1420px, 96vw); padding: 26px 0; }
      /* The sliders were the browser's, in the browser's blue. */
      #menu input[type="range"] { accent-color: var(--gold); padding: 0; height: 22px;
        background: transparent; border: 0; }
      #menu .title { font-family: 'Cinzel', Georgia, serif; font-size: 46px; letter-spacing: 0.4em;
        text-transform: uppercase; color: var(--gold); text-align: center;
        text-shadow: 0 0 60px rgba(200,164,76,0.28); }
      #menu .tagline { text-align: center; color: var(--bone-deep); font-style: italic;
        margin: 12px 0 34px; font-size: 13.5px; letter-spacing: 0.04em; }
      #menu .modes { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
      #menu .mode { padding: 24px; cursor: pointer; background: rgba(255,255,255,0.028);
        border: 1px solid rgba(200,164,76,0.22);
        clip-path: polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px);
        transition: background 150ms ease, border-color 150ms ease; }
      #menu .mode:hover { background: rgba(200,164,76,0.12); border-color: var(--gold); }
      #menu .mode h2 { font-size: 17px; letter-spacing: 0.24em; margin-bottom: 8px; }
      #menu .mode p { font-size: 12.5px; color: var(--bone-dim); line-height: 1.6; }
      #menu .mode .tag { font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase;
        color: var(--ember); margin-top: 12px; }
      #menu .row { margin-bottom: 22px; }
      #menu .row > .lbl { font-family: 'Cinzel', Georgia, serif; font-size: 11px; letter-spacing: 0.22em;
        text-transform: uppercase; color: var(--gold-deep); margin-bottom: 8px; }
      #menu .opts { display: flex; flex-wrap: wrap; gap: 7px; }
      #menu .opt { padding: 9px 13px; cursor: pointer; font-size: 12px;
        background: rgba(255,255,255,0.03); border: 1px solid rgba(200,164,76,0.16);
        clip-path: polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%); min-width: 96px; }
      #menu .opt:hover { background: rgba(200,164,76,0.1); }
      #menu .opt.on { background: rgba(200,164,76,0.2); border-color: var(--gold); color: var(--gold-pale); }
      #menu .opt .sub { display: block; font-size: 10.5px; color: var(--bone-deep); margin-top: 3px; font-style: italic; }
      /* ── the co-op lobby ─────────────────────────────────────────────── */
      #menu .co-in { width: 100%; max-width: 420px; padding: 9px 12px; font: inherit; font-size: 13px;
        color: var(--bone); background: rgba(255,255,255,0.03); border: 1px solid rgba(200,164,76,0.16);
        outline: none; }
      #menu .co-in:focus { border-color: var(--gold); background: rgba(200,164,76,0.08); }
      #menu .co-in::placeholder { color: var(--bone-deep); font-style: italic; }
      #menu .co-split { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; margin: 6px 0 14px; }
      @media (max-width: 780px) { #menu .co-split { grid-template-columns: 1fr; } }
      #menu .co-side { border: 1px solid rgba(200,164,76,0.14); padding: 16px 18px 18px;
        background: rgba(255,255,255,0.015); }
      #menu .co-side h3 { font-family: 'Cinzel', Georgia, serif; font-size: 13px; letter-spacing: 0.2em;
        text-transform: uppercase; color: var(--gold); margin: 0 0 12px; font-weight: 600; }
      #menu .co-note { font-size: 11.5px; color: var(--bone-deep); font-style: italic; line-height: 1.5;
        margin: 12px 0 0; }
      #menu .co-rooms { display: flex; flex-direction: column; gap: 7px; min-height: 96px;
        max-height: 210px; overflow-y: auto; font-size: 12px; color: var(--bone-deep); }
      #menu .co-room { padding: 9px 12px; cursor: pointer; background: rgba(255,255,255,0.03);
        border: 1px solid rgba(200,164,76,0.16); }
      #menu .co-room:hover { background: rgba(200,164,76,0.1); border-color: var(--gold); }
      #menu .co-room b { display: block; color: var(--bone); font-size: 13px; font-weight: 600; }
      #menu .co-room span { font-size: 11px; color: var(--bone-deep); }
      #menu .co-room.full { opacity: 0.45; cursor: not-allowed; }
      #menu .co-room.full:hover { background: rgba(255,255,255,0.03); border-color: rgba(200,164,76,0.16); }
      #menu .co-resume { border: 1px solid rgba(200,164,76,0.14); padding: 16px 18px 18px;
        background: rgba(255,255,255,0.015); margin: 0 0 12px; }
      #menu .co-resume h3 { font-family: 'Cinzel', Georgia, serif; font-size: 13px;
        letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold); margin: 0 0 6px;
        font-weight: 600; }
      #menu .co-resume .co-note { margin: 0 0 12px; }
      #menu .co-rooms.wide { max-height: 168px; min-height: 46px; }
      #menu .co-empty { padding: 10px 0; font-style: italic; }
      #menu .co-err { min-height: 18px; font-size: 12px; color: var(--oxblood-light, #9c3336); margin: 4px 0 2px; }
      #menu .go { display: flex; align-items: center; gap: 14px; margin-top: 8px; }
      #menu .back { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--bone-deep); cursor: pointer; }
      #menu .back:hover { color: var(--bone); }
      #menu .seedline { font-size: 11px; color: var(--bone-deep); font-variant-numeric: tabular-nums; }
      #menu .brief { font-size: 12px; color: var(--bone-dim); line-height: 1.6; margin-top: 6px;
        padding: 10px 12px; background: rgba(0,0,0,0.3); border-left: 2px solid var(--gold-deep); }
      #menu .keys { margin-top: 30px; font-size: 11px; color: var(--bone-deep); line-height: 1.8;
        border-top: 1px solid rgba(200,164,76,0.14); padding-top: 14px; }
      #menu .keys b { color: var(--bone-dim); font-weight: 500; }
      #menu .hero-opt { min-width: 118px; padding: 8px 8px 10px; text-align: center;
        display: flex; flex-direction: column; align-items: center; gap: 4px; }
      #menu .hero-opt .nm { font-family: 'Cinzel', Georgia, serif; font-size: 12px;
        letter-spacing: 0.14em; text-transform: uppercase; color: var(--bone); }
      #menu .hero-opt.on .nm { color: var(--gold-pale); }
      #menu .hero-opt .sub { max-width: 122px; line-height: 1.35; margin-top: 0; }
      #menu .empty-niche { width: 104px; height: 125px;
        background: linear-gradient(#1b1917, #0f0e0c);
        border: 1px solid rgba(200,164,76,0.22);
        border-radius: 52px 52px 0 0; }

      /* ── The setup flow ─────────────────────────────────────────────────── */
      #menu .steps { display: flex; gap: 10px; align-items: center; justify-content: center;
        font-family: 'Cinzel', Georgia, serif; font-size: 10.5px; letter-spacing: 0.2em;
        text-transform: uppercase; color: var(--bone-deep); margin: 10px 0 26px; }
      #menu .steps i { font-style: normal; color: var(--bone-deep); }
      #menu .steps i.on { color: var(--gold); }
      #menu .steps i.done { color: var(--bone-dim); cursor: pointer; }
      #menu .steps i.done:hover { color: var(--gold-pale); }
      #menu .steps s { text-decoration: none; opacity: 0.4; }

      /* The realm step. */
      #menu .realm-card { display: grid; grid-template-columns: 1fr 300px; gap: 20px;
        padding: 20px 22px; background: rgba(255,255,255,0.03);
        border: 1px solid var(--gold-deep); }
      #menu .realm-card h3 { font-family: 'Cinzel', Georgia, serif; font-size: 19px;
        letter-spacing: 0.16em; color: var(--gold-pale); margin-bottom: 6px; }
      #menu .realm-card p { font-size: 12.5px; color: var(--bone-dim); line-height: 1.65; }
      #menu .facts { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px 14px; margin-top: 14px; }
      #menu .fact { border-left: 2px solid rgba(200,164,76,0.3); padding-left: 9px; }
      #menu .fact b { display: block; font-family: 'Cinzel', Georgia, serif; font-size: 17px;
        color: var(--bone); font-variant-numeric: tabular-nums; }
      #menu .fact span { font-size: 10.5px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--bone-deep); }
      #menu .seam { margin-top: 18px; padding: 12px 14px; font-size: 11.5px; line-height: 1.65;
        color: var(--bone-deep); background: rgba(0,0,0,0.32);
        border: 1px dashed rgba(200,164,76,0.26); }
      #menu .seam b { color: var(--gold-deep); letter-spacing: 0.12em; text-transform: uppercase;
        font-size: 10px; display: block; margin-bottom: 5px; }

      /* The kingdom step: a rail of twenty-two, and one large sheet. */
      #menu .kingdom { display: grid; grid-template-columns: 322px 1fr; gap: 22px; align-items: start; }
      #menu .rail { max-height: 76vh; overflow-y: auto; padding-right: 8px; }
      #menu .rail::-webkit-scrollbar { width: 10px; }
      #menu .rail::-webkit-scrollbar-track { background: rgba(0,0,0,0.35); }
      #menu .rail::-webkit-scrollbar-thumb { background: rgba(200,164,76,0.3); }
      #menu .rail::-webkit-scrollbar-thumb:hover { background: var(--gold-deep); }
      #menu .kin { font-family: 'Cinzel', Georgia, serif; font-size: 10px; letter-spacing: 0.26em;
        text-transform: uppercase; color: var(--gold-deep); margin: 16px 0 6px;
        border-bottom: 1px solid rgba(200,164,76,0.16); padding-bottom: 4px; }
      #menu .kin:first-child { margin-top: 0; }
      #menu .kd { display: grid; grid-template-columns: 3px auto 1fr; gap: 10px; align-items: center;
        padding: 5px 8px 5px 0; cursor: pointer; border: 1px solid transparent;
        transition: background 120ms ease, border-color 120ms ease; }
      #menu .kd:hover { background: rgba(200,164,76,0.08); }
      #menu .kd.on { background: rgba(200,164,76,0.14); border-color: var(--gold-deep); }
      #menu .kd .banner { align-self: stretch; }
      #menu .kd .nm { font-family: 'Cinzel', Georgia, serif; font-size: 12.5px; letter-spacing: 0.1em;
        color: var(--bone); line-height: 1.3; }
      #menu .kd.on .nm { color: var(--gold-pale); }
      #menu .kd .of { font-size: 10.5px; color: var(--bone-deep); line-height: 1.4; }
      #menu .kd .bind { display: inline-block; margin-top: 3px; font-size: 9.5px; letter-spacing: 0.13em;
        text-transform: uppercase; padding: 1px 6px; color: var(--bone-dim);
        border: 1px solid rgba(200,164,76,0.24); background: rgba(0,0,0,0.3); }

      #menu .sheet { max-height: 76vh; overflow-y: auto; padding-right: 10px; }
      #menu .sheet::-webkit-scrollbar { width: 10px; }
      #menu .sheet::-webkit-scrollbar-track { background: rgba(0,0,0,0.35); }
      #menu .sheet::-webkit-scrollbar-thumb { background: rgba(200,164,76,0.3); }
      #menu .head { display: grid; grid-template-columns: auto 1fr; gap: 24px; align-items: end; }
      #menu .head h2 { font-family: 'Cinzel', Georgia, serif; font-size: 30px; letter-spacing: 0.1em;
        color: var(--gold-pale); line-height: 1.15; }
      #menu .head .who { font-family: 'Cinzel', Georgia, serif; font-size: 15px; letter-spacing: 0.16em;
        text-transform: uppercase; color: var(--bone); margin-top: 10px; }
      #menu .head .ep { font-size: 12.5px; font-style: italic; color: var(--bone-deep); margin-top: 4px; }
      #menu .head .meta { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 14px; }
      #menu .tagchip { font-size: 10px; letter-spacing: 0.13em; text-transform: uppercase;
        padding: 3px 8px; color: var(--bone-dim); border: 1px solid rgba(200,164,76,0.22);
        background: rgba(0,0,0,0.3); }
      #menu .head .fl { font-size: 12.5px; color: var(--bone-dim); line-height: 1.65; margin-top: 12px;
        font-style: italic; }

      #menu .band { margin-top: 20px; padding: 14px 16px; background: rgba(0,0,0,0.34);
        border-left: 3px solid var(--gold); }
      #menu .band .cap { font-family: 'Cinzel', Georgia, serif; font-size: 10px; letter-spacing: 0.26em;
        text-transform: uppercase; color: var(--gold-deep); margin-bottom: 6px; }
      #menu .band h4 { font-family: 'Cinzel', Georgia, serif; font-size: 17px; letter-spacing: 0.12em;
        color: var(--gold-pale); margin-bottom: 6px; }
      #menu .band p { font-size: 12.5px; color: var(--bone-dim); line-height: 1.7; }
      #menu .unbuilt { margin-top: 8px; font-size: 11px; font-style: italic; color: var(--ember); opacity: 0.85; }

      #menu .cols { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 18px; }
      #menu .col .cap { font-family: 'Cinzel', Georgia, serif; font-size: 10px; letter-spacing: 0.22em;
        text-transform: uppercase; color: var(--gold-deep); margin-bottom: 8px;
        border-bottom: 1px solid rgba(200,164,76,0.16); padding-bottom: 4px; }
      #menu .col li { list-style: none; font-size: 11.5px; color: var(--bone-dim); line-height: 1.55;
        margin-bottom: 7px; }
      #menu .col li b { color: var(--bone); font-weight: 500; }
      #menu .col li i { display: block; font-size: 10.5px; color: var(--bone-deep); font-style: italic; }
      #menu .res { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 8px; }
      #menu .res span { font-size: 10.5px; letter-spacing: 0.08em; padding: 2px 7px;
        border: 1px solid rgba(200,164,76,0.28); color: var(--gold-pale); background: rgba(200,164,76,0.1); }
      #menu .res span.no { color: var(--bone-deep); border-color: rgba(255,255,255,0.1);
        background: rgba(0,0,0,0.3); text-decoration: line-through; }
      #menu .num { display: flex; justify-content: space-between; gap: 10px; font-size: 11.5px;
        color: var(--bone-dim); line-height: 1.7; font-variant-numeric: tabular-nums; }
      #menu .num b { font-weight: 500; }
      #menu .num b.up { color: var(--hp-good, #9fbe5a); }
      #menu .num b.dn { color: #c2705f; }

      #menu .layers { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 18px; }
      #menu .layer { padding: 12px 14px; background: rgba(255,255,255,0.026);
        border: 1px solid rgba(200,164,76,0.16); }
      #menu .layer .cap { font-family: 'Cinzel', Georgia, serif; font-size: 10px; letter-spacing: 0.2em;
        text-transform: uppercase; color: var(--gold-deep); margin-bottom: 3px; }
      #menu .layer .capsub { font-size: 10.5px; font-style: italic; color: var(--bone-deep); margin-bottom: 10px; }
      #menu .layer .pv { margin-bottom: 10px; }
      #menu .layer .pv b { display: block; font-family: 'Cinzel', Georgia, serif; font-size: 12px;
        letter-spacing: 0.1em; color: var(--bone); margin-bottom: 2px; }
      #menu .layer .pv span { font-size: 11.5px; color: var(--bone-dim); line-height: 1.6; }

      #menu .rival { margin-top: 16px; font-size: 11.5px; color: var(--bone-deep); line-height: 1.7;
        padding: 10px 12px; background: rgba(0,0,0,0.28); border-left: 2px solid rgba(200,164,76,0.28); }
      #menu .rival b { color: var(--bone-dim); }
      #menu .rival u { text-decoration: none; color: var(--gold-pale); cursor: pointer; }
      #menu .rival u:hover { text-decoration: underline; }
      /* Outside the scrolling sheet, not sticky inside it. A sticky footer over
         a panel this long spends most of its life covering the line the reader
         is on, and the line it covered was the honest one about unit rosters. */
      #menu .foot { display: flex; align-items: center; gap: 16px; margin-top: 16px;
        padding-top: 14px; border-top: 1px solid rgba(200,164,76,0.16); }

      /* Save slots. Small on purpose: the newest run keeps the big Continue
         card, and the others are a shelf beneath it rather than four more
         things competing with "The Long Night" for the eye. */
      #menu .slots { grid-column: 1/-1; display: flex; flex-wrap: wrap; gap: 8px; margin-top: -4px; }
      #menu .slot { flex: 1 1 180px; padding: 8px 12px; cursor: pointer;
        background: rgba(255,255,255,0.026); border: 1px solid rgba(200,164,76,0.16); }
      #menu .slot:hover { border-color: var(--gold); background: rgba(200,164,76,0.08); }
      #menu .slot b { display: block; font-family: 'Cinzel', Georgia, serif; font-size: 11px;
        letter-spacing: 0.18em; text-transform: uppercase; color: var(--gold-deep); }
      #menu .slot span { display: block; font-size: 11.5px; color: var(--bone-dim); margin-top: 3px; }
      #menu .slot i { display: block; font-size: 10.5px; color: var(--bone-deep); font-style: italic; }
      #menu .saveline { margin-top: 12px; font-size: 10.5px; letter-spacing: 0.04em;
        color: var(--bone-deep); line-height: 1.6; }
      #menu .saveline b { color: var(--bone-dim); font-weight: 500; }

      /* ── The Bloodline ──────────────────────────────────────────────────── */
      #menu .bl-head { display: grid; grid-template-columns: repeat(4, auto) 1fr; gap: 10px 22px;
        align-items: start; padding: 14px 16px; background: rgba(255,255,255,0.03);
        border: 1px solid var(--gold-deep); margin-bottom: 18px; }
      #menu .bl-how { font-size: 11.5px; color: var(--bone-deep); line-height: 1.65; max-width: 520px; }
      #menu .bl-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
      #menu .bl-branch { padding: 12px 14px; background: rgba(255,255,255,0.026);
        border: 1px solid rgba(200,164,76,0.16); }
      #menu .bl-branch .cap { font-family: 'Cinzel', Georgia, serif; font-size: 11px;
        letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-deep);
        border-bottom: 1px solid rgba(200,164,76,0.16); padding-bottom: 5px; }
      #menu .bl-fl { font-size: 10.5px; font-style: italic; color: var(--bone-deep);
        margin: 7px 0 10px; line-height: 1.5; }
      #menu .bl-step { padding: 9px 10px; margin-bottom: 8px; background: rgba(0,0,0,0.28);
        border-left: 2px solid rgba(200,164,76,0.2); }
      #menu .bl-step b { display: block; font-family: 'Cinzel', Georgia, serif; font-size: 12px;
        letter-spacing: 0.1em; color: var(--bone); margin-bottom: 3px; }
      #menu .bl-step span { display: block; font-size: 11px; color: var(--bone-dim); line-height: 1.5; }
      #menu .bl-step i { display: block; font-size: 10px; font-style: italic;
        color: var(--bone-deep); margin-top: 3px; line-height: 1.45; }
      #menu .bl-step.owned { border-left-color: var(--gold); background: rgba(200,164,76,0.08); }
      #menu .bl-step.owned b { color: var(--gold-pale); }
      #menu .bl-step.locked { opacity: 0.55; }
      #menu .bl-held { display: inline-block; margin-top: 6px; font-size: 9.5px;
        letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold); }
      #menu .bl-cost { display: inline-block; margin-top: 6px; font-size: 10px;
        letter-spacing: 0.08em; color: var(--bone-deep); font-variant-numeric: tabular-nums; }
      #menu .bl-buy { display: inline-block; margin-top: 7px; font-size: 11px; }

      /* ── The title screen, and the lists that hang off it ───────────────
         A backdrop, a name, a column of words, and one sentence at the foot
         about whichever word you are on. The cards this replaced said all of
         it at once, which is six paragraphs to read before the first click.

         Everything here is prefixed \`tt-\` and used by nothing else, so the
         setup flow's rules above are untouched. */
      #menu.tt-mode { align-items: stretch; overflow: hidden; }
      #menu .splash { position: absolute; inset: 0; z-index: 0;
        background-size: cover; background-position: center;
        opacity: 0; transition: opacity 700ms ease; }
      #menu .splash.in { opacity: 1; }
      /* The wash that keeps gold text legible over a painting nobody has seen
         yet. Heaviest at top and bottom, where the name and the hint sit. */
      #menu .splash::after { content: ''; position: absolute; inset: 0;
        background: linear-gradient(180deg, rgba(8,7,6,0.80) 0%, rgba(8,7,6,0.40) 32%,
          rgba(8,7,6,0.52) 60%, rgba(8,7,6,0.93) 100%); }

      #menu .tt { position: relative; z-index: 1; width: min(880px, 94vw);
        display: grid; grid-template-rows: 1fr auto 1fr; justify-items: center;
        padding: 40px 0 22px; }
      #menu .tt-brand { align-self: end; text-align: center; padding-bottom: 26px; }
      #menu .tt-title { font-family: 'Cinzel', Georgia, serif;
        font-size: clamp(30px, 5.6vw, 58px); line-height: 1.16;
        letter-spacing: 0.26em; text-transform: uppercase; color: var(--bone);
        /* The tracking is added after the last letter too, so without this the
           block sits half a letter left of the centre it is centred on. */
        margin-right: -0.26em;
        text-shadow: 0 2px 30px rgba(0,0,0,0.92), 0 0 76px rgba(200,164,76,0.20); }
      #menu .tt-sub { margin-top: 14px; font-size: 13px; font-style: italic;
        letter-spacing: 0.04em; color: var(--bone-deep);
        text-shadow: 0 1px 12px rgba(0,0,0,0.9); }

      #menu .tt-mid { align-self: center; display: flex; flex-direction: column;
        align-items: center; gap: 28px; width: 100%; }
      #menu .tt-list { display: flex; flex-direction: column; width: min(360px, 82vw); }
      #menu .tt-item { display: block; width: 100%; appearance: none; cursor: pointer;
        background: transparent; border: 1px solid transparent; outline: none;
        padding: 10px 14px; text-align: center; font: inherit;
        font-family: 'Cinzel', Georgia, serif; font-size: 14px; font-weight: 600;
        letter-spacing: 0.2em; text-transform: uppercase; color: var(--bone-dim);
        text-shadow: 0 1px 10px rgba(0,0,0,0.85);
        transition: color 120ms ease, border-color 120ms ease, background 120ms ease; }
      /* Hover and focus are the same state on purpose: the mouse moves the
         focus (see \`wireList\`), so there is only ever one lit row and the
         keyboard and the mouse cannot disagree about which one it is. */
      #menu .tt-item:focus { color: var(--gold-pale); border-color: var(--gold);
        background: rgba(200,164,76,0.10); }
      #menu .tt-item .tt-note { display: block; margin-top: 4px;
        font-family: 'Inter', system-ui, sans-serif; font-size: 11px; font-weight: 400;
        letter-spacing: 0.03em; text-transform: none; font-style: italic;
        color: var(--bone-deep); }
      #menu .tt-item:focus .tt-note { color: var(--bone-dim); }

      #menu .tt-foot { align-self: end; text-align: center; }
      /* Reserved whether or not there is a sentence in it, so moving down the
         list does not shift the list. */
      #menu .tt-hint { min-height: 19px; font-size: 12.5px; line-height: 1.5;
        color: var(--bone-dim); letter-spacing: 0.03em;
        text-shadow: 0 1px 10px rgba(0,0,0,0.9); }
      #menu .tt-legend { margin-top: 12px; font-size: 10px; letter-spacing: 0.2em;
        text-transform: uppercase; color: var(--bone-deep); }
      #menu .tt-corner { position: absolute; left: 16px; bottom: 14px; z-index: 1;
        max-width: 46vw; font-size: 10.5px; line-height: 1.5; color: var(--bone-deep); }
      #menu .tt-corner b { color: var(--bone-dim); font-weight: 500; }
      @media (max-height: 620px) {
        #menu .tt-sub, #menu .tt-corner { display: none; }
        #menu .tt-brand { padding-bottom: 14px; }
        #menu .tt-item { padding: 7px 14px; }
      }

      /* Credits, and the keyboard on the Options screen. */
      #menu .tt-prose { width: min(560px, 88vw);
        font-size: 12.5px; line-height: 1.75; color: var(--bone-dim); }
      #menu .tt-prose h4 { font-family: 'Cinzel', Georgia, serif; font-size: 10.5px;
        letter-spacing: 0.24em; text-transform: uppercase; color: var(--gold-deep);
        margin: 20px 0 6px; }
      #menu .tt-prose h4:first-child { margin-top: 0; }
      #menu .keysheet { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px 24px; }
      @media (max-width: 720px) { #menu .keysheet { grid-template-columns: 1fr; } }
      #menu .keysheet .kg { font-family: 'Cinzel', Georgia, serif; font-size: 10px;
        letter-spacing: 0.22em; text-transform: uppercase; color: var(--gold-deep);
        border-bottom: 1px solid rgba(200,164,76,0.16); padding-bottom: 4px; margin-bottom: 6px; }
      #menu .keysheet .krow { display: grid; grid-template-columns: 96px 1fr; gap: 10px;
        font-size: 11.5px; color: var(--bone-dim); line-height: 1.6; }
      #menu .keysheet .kk { color: var(--bone); font-variant-numeric: tabular-nums; }
    `;
    document.head.appendChild(s);
  }

  hide(): void {
    this.visible = false;
    this.el.classList.add('gone');
  }

  show(screen: 'title' | 'bloodline' = 'title'): void {
    this.visible = true;
    this.el.classList.remove('gone');
    this.render(screen);
  }

  get isOpen(): boolean {
    return this.visible;
  }

  private render(screen: Screen): void {
    // Both are set by `renderList` for the screens that want them, so every
    // other screen has to be handed them back cleared.
    this.escapeTo = null;
    this.el.onmousedown = null;
    // The list screens are full-bleed and centre themselves; the setup flow's
    // screens are a centred column that scrolls.
    this.el.classList.toggle('tt-mode', screen === 'title' || screen === 'newgame' ||
      screen === 'load' || screen === 'credits');
    if (screen === 'title') return this.renderTitle();
    if (screen === 'newgame') return this.renderNewGame();
    if (screen === 'load') return this.renderLoad();
    if (screen === 'credits') return this.renderCredits();
    if (screen === 'realm') return this.renderRealm();
    if (screen === 'kingdom') return this.renderKingdom(true);
    if (screen === 'siege-kingdom') return this.renderKingdom(false);
    if (screen === 'settings') return this.renderSettings();
    if (screen === 'coop') return this.renderCoop();
    if (screen === 'coop-kingdom') return this.renderKingdom(false, true);
    if (screen === 'bloodline') return this.renderBloodline();
    if (screen === 'codex') return this.renderCodex();
    return this.renderBattle();
  }

  /**
   * The title screen: a backdrop, the name, and a column of words.
   *
   * This was six cards of prose, and the prose was the problem. The first
   * thing anyone met was several paragraphs to read before anything could be
   * clicked, and the one thing a returning player had come back for was one
   * card among six with no more weight than the rest.
   *
   * What is here instead is the arrangement games with menus have long since
   * converged on, and for reasons that all apply here: the resume is first and
   * already focused, the rest is a short list of single words, and the
   * explanation for exactly one of them — whichever the player is on — is at
   * the foot of the screen.
   *
   * None of the deleted prose was actually deleted. It moved into `hint`, and
   * is now said one sentence at a time about the row being looked at, which is
   * the only rate at which anybody was ever going to read it.
   */
  private renderTitle(): void {
    /**
     * Saves, newest first. `Continue` takes the newest and is the default
     * focus; `Load Game` opens the rest.
     *
     * Both rows are absent on a first run rather than present and dead. A
     * greyed row is still a word the player has to read and discard every
     * single time they come home, and a list of five live words is the whole
     * point of this screen.
     */
    const saves = listSaves();
    const newest = saves[0];
    const items: MenuItem[] = [];
    if (newest) {
      items.push({
        label: 'Continue',
        hint: `${faction(newest.faction).name} — ${savePosition(newest)}, saved ${ago(newest.savedAt)}.`,
        go: 'continue',
        slot: newest.slot,
      });
    }
    items.push({
      label: 'New Game',
      hint: 'Begin again — the campaign, the settlement, or a single field.',
      go: 'newgame',
    });
    // Directly under New Game, and a door of its own rather than a branch of
    // one. A room is a way to start a run, not a kind of run.
    items.push({
      label: 'Co-op',
      hint: 'The campaign with other crowns in it that are not the machine’s. Two to four players, a kingdom each, one realm.',
      go: 'coop',
    });
    if (saves.length > 0) {
      items.push({
        label: 'Load Game',
        hint: `${saves.length} saved run${saves.length === 1 ? '' : 's'}. Go back to whichever one you like.`,
        go: 'load',
      });
    }
    items.push(
      { label: 'The Bloodline', hint: this.bloodlineHint(), go: 'bloodline' },
      {
        label: 'The Codex',
        hint: 'Every soldier, building, power and relic in the catalogue, with its exact rules.',
        go: 'codex',
      },
      {
        label: 'Options',
        hint: 'Sound, the noise of a siege, and every key the game listens to.',
        go: 'settings',
      },
      { label: 'Credits', hint: 'Who built it, and what it stands on.', go: 'credits' },
    );

    const store = storageStatus();
    this.renderList({
      title: 'The Last Hearth',
      sub: 'It has not gone out. That is the whole of the realm’s good news.',
      items,
      corner:
        `Saves: <b>${esc(store.location)}</b>` +
        (store.note ? ` — ${esc(store.note)}` : '') +
        (didMigrate() ? ' Your browser save was copied here.' : ''),
      on: (it) => {
        if (it.go === 'continue') {
          this.cb.onContinue(it.slot ?? 1);
          this.hide();
          return;
        }
        this.render(it.go as Screen);
      },
    });
  }

  /**
   * The ways in, named for what they are.
   *
   * The rows used to be the modes' own names — *The Long Night*, *Hold the
   * Hearth*, *A Field and Two Armies* — which are the right names everywhere
   * except here. A menu row is read by somebody who does not yet know what the
   * game contains, and three evocative names tell them nothing about which of
   * the three is the campaign. So the row says what the mode *is* and the hint
   * carries the name, rather than the other way round. Every screen the rows
   * open still calls itself by its own name.
   */
  private renderNewGame(): void {
    this.renderList({
      title: 'New Game',
      items: [
        {
          label: 'Main Campaign',
          hint: 'The Long Night — the realm and everything in it. Choose a kingdom, build your city, and defend the one you actually built.',
          go: 'realm',
        },
        {
          label: 'City Building',
          hint: 'Hold the Hearth — one settlement, no realm around it, and no end written anywhere. You find out how long you stand.',
          go: 'siege-kingdom',
        },
        {
          label: 'Battles',
          hint: 'A field and two armies. No economy, no walls, no Hearth — win it with morale, flanks and fatigue.',
          go: 'battle',
        },
        {
          /**
           * The guided run. Five real waves with their parameters bent — fewer
           * of them, from the gate you just walled — so that the verb the card
           * teaches is the verb the next wave asks for. The hint carries what
           * this player has already done, read from `hearth.tutorial.v1`, which
           * is a person's progress rather than a run's and so lives outside
           * every save.
           */
          label: 'Learn the Game',
          hint:
            'One settlement, five guided waves, and the whole loop taught one verb at a time — build, wall, muster, command, cast. Skippable at any point; what is left behind is a real run.' +
            ((n) => (n ? ` ${n}` : ''))(tutorialMenuNote(tutorialProgress().state())),
          go: 'tutorial',
        },
        { label: 'Back', hint: '', go: 'title' },
      ],
      back: 'title',
      on: (it) => {
        if (it.go === 'tutorial') {
          this.cb.onTutorial();
          this.hide();
          return;
        }
        this.render(it.go as Screen);
      },
    });
  }

  /**
   * Every save, as its own row.
   *
   * This is where the shelf of small slot cards under the old Continue card
   * went. It is a better home for them: they were competing with the modes for
   * the eye on a screen whose job was to offer modes, and a player who wants a
   * particular run rather than the newest one has said so by the time they get
   * here.
   */
  private renderLoad(): void {
    const saves = listSaves();
    const items: MenuItem[] = saves.map((s) => ({
      label: s.campaign ? 'The Long Night' : 'Hold the Hearth',
      note: `${faction(s.faction).name} — ${savePosition(s)}`,
      hint: `Slot ${s.slot}, saved ${ago(s.savedAt)}.`,
      go: 'continue',
      slot: s.slot,
    }));
    items.push({ label: 'Back', hint: '', go: 'title' });
    this.renderList({
      title: 'Load Game',
      sub: saves.length > 0 ? undefined : 'Nothing has been saved yet.',
      items,
      back: 'title',
      on: (it) => {
        if (it.go === 'continue') {
          this.cb.onContinue(it.slot ?? 1);
          this.hide();
          return;
        }
        this.render(it.go as Screen);
      },
    });
  }

  /** What it is, and what it stands on. No third-party asset to credit. */
  private renderCredits(): void {
    this.renderList({
      title: 'Credits',
      prose:
        `<h4>The Last Hearth</h4>` +
        `<p>A dark-medieval-fantasy city-builder and siege defence. Eleven peoples, twenty-two
          kingdoms, one realm, and a night that does not stop coming.</p>` +
        `<h4>Made with</h4>` +
        `<p>TypeScript and Vite, with PixiJS drawing the field and Tauri wrapping the desktop
          build. Cinzel and Inter, both from the Fontsource packages.</p>` +
        `<h4>The art</h4>` +
        `<p>Every leader, soldier and building is drawn by the game itself, in vector, at run
          time — which is the guarantee that there is always a face. Painted art, where it
          exists, is dropped in over the top and changes nothing else.</p>`,
      items: [{ label: 'Back', hint: '', go: 'title' }],
      back: 'title',
      on: (it) => this.render(it.go as Screen),
    });
  }

  /**
   * The furniture every list screen shares: the backdrop, the name, the rows,
   * and the one sentence at the foot.
   *
   * The mouse moves the focus rather than lighting a row of its own, so there
   * is exactly one lit row however the screen is being driven and the hint
   * cannot end up describing a different row from the one that looks chosen.
   */
  private renderList(o: {
    title: string;
    sub?: string;
    /** Trusted markup above the list. Built here; no player text reaches it. */
    prose?: string;
    items: MenuItem[];
    /** Trusted markup for the bottom-left line, same rule as `prose`. */
    corner?: string;
    /** Where Escape goes. Absent on the title screen, which has no up. */
    back?: Screen;
    on(item: MenuItem): void;
  }): void {
    this.escapeTo = o.back ?? null;
    const rows = o.items
      .map(
        // `data-go` is not read by anything here — the click handler closes
        // over the item. It is on the row for the shot harness, which drives
        // these screens by selector and has no other handle on them.
        (it) =>
          `<button type="button" class="tt-item" data-go="${esc(it.go)}">${esc(it.label)}` +
          (it.note ? `<span class="tt-note">${esc(it.note)}</span>` : '') +
          `</button>`,
      )
      .join('');
    this.el.innerHTML =
      `<div class="splash" id="tt-splash"></div>` +
      `<div class="tt">` +
      `<div class="tt-brand"><h1 class="tt-title">${esc(o.title)}</h1>` +
      (o.sub ? `<div class="tt-sub">${esc(o.sub)}</div>` : '') +
      `</div>` +
      `<div class="tt-mid">` +
      (o.prose ? `<div class="tt-prose">${o.prose}</div>` : '') +
      `<nav class="tt-list">${rows}</nav>` +
      `</div>` +
      `<div class="tt-foot"><div class="tt-hint" id="tt-hint"></div>` +
      `<div class="tt-legend">↑ ↓ Move &nbsp;·&nbsp; Enter Select` +
      (o.back ? ` &nbsp;·&nbsp; Esc Back` : '') +
      `</div></div>` +
      `</div>` +
      (o.corner ? `<div class="tt-corner">${o.corner}</div>` : '');

    const hint = this.el.querySelector('#tt-hint') as HTMLElement;
    const buttons = [...this.el.querySelectorAll<HTMLButtonElement>('.tt-item')];
    buttons.forEach((btn, i) => {
      btn.onfocus = () => {
        hint.textContent = o.items[i].hint;
      };
      btn.onmouseenter = () => btn.focus();
      btn.onclick = () => o.on(o.items[i]);
    });
    if (buttons.length > 0) buttons[0].focus();
    /**
     * A click on the backdrop would otherwise move the focus to `<body>`, and
     * the arrow keys would quietly stop working with nothing on screen to
     * explain why — the keydown listener is on this element, and a focus
     * outside it never reaches here.
     */
    this.el.onmousedown = (e) => {
      if (!(e.target as HTMLElement).closest('.tt-item')) e.preventDefault();
    };
    this.hydrateSplash();
  }

  /**
   * The painted backdrop, remembered for the session — this is a screen the
   * player leaves and comes back to, and one probe is enough.
   *
   * `null` is "not looked yet", an image is the file, `'missing'` is the
   * normal case today: `art/splash/menu.png` is reserved in ARTBRIEF.md and
   * has never been generated. Missing costs one failed probe and leaves the
   * radial gradient underneath, which is the background this screen was
   * designed against rather than a degraded version of it.
   *
   * Probed through `new Image()` for the same reason `artdrop` does it: the
   * desktop shell's CSP, and a 404 that does not have to be loud.
   */
  private static splash: HTMLImageElement | 'missing' | null = null;

  private hydrateSplash(): void {
    const el = this.el.querySelector('#tt-splash') as HTMLElement | null;
    if (!el) return;
    const paint = (img: HTMLImageElement, fade: boolean): void => {
      if (!el.isConnected) return;
      // Only the first sighting fades in. Re-fading every time the player
      // steps back to the title reads as the screen loading again.
      if (!fade) el.style.transition = 'none';
      el.style.backgroundImage = `url("${img.src}")`;
      el.classList.add('in');
    };
    const hit = Menu.splash;
    if (hit === 'missing') return;
    if (hit) {
      paint(hit, false);
      return;
    }
    const img = new Image();
    img.onload = () => {
      Menu.splash = img;
      paint(img, true);
    };
    img.onerror = () => {
      Menu.splash = 'missing';
    };
    img.src = 'art/splash/menu.png';
  }

  /**
   * The Bloodline's row on the title screen. Always present — a
   * meta-progression nobody can find is one that does not exist — and the
   * hint carries the bank's headline, so coming home from a death shows the
   * number moving.
   */
  private bloodlineHint(): string {
    const b = bloodline().state();
    if (b.earnedTotal <= 0) {
      return 'Nothing is banked yet. Every run that ends pays the family by how deep into the dark it stood.';
    }
    return (
      `${b.legacy} Legacy held, of ${b.earnedTotal} ever banked. ` +
      `${b.runs} run${b.runs === 1 ? '' : 's'} remembered; the deepest reached wave ${b.bestWave}.`
    );
  }

  /**
   * The spend screen: four ladders, climbed from the bottom. Deliberately not
   * a graph — each branch is a straight line of five, so a column per branch
   * *is* the tree, and the eye can price the whole thing in one pass.
   */
  private renderBloodline(): void {
    const bank = bloodline().state();
    const rows = unlockRows(bank);
    const byBranch = new Map<string, UnlockRow[]>();
    for (const r of rows) {
      const list = byBranch.get(r.def.branch) ?? [];
      list.push(r);
      byBranch.set(r.def.branch, list);
    }
    const stateLine = (r: UnlockRow): string => {
      if (r.state === 'owned') return `<span class="bl-held">Held</span>`;
      if (r.state === 'ready') return `<span class="seal bl-buy" data-buy="${r.def.id}">Take it — ${r.def.cost}</span>`;
      if (r.state === 'poor') return `<span class="bl-cost">${r.def.cost} Legacy</span>`;
      return `<span class="bl-cost">${r.def.cost} Legacy · the step below first</span>`;
    };
    const cols = BLOODLINE_BRANCHES.map((br) => {
      const list = byBranch.get(br.id) ?? [];
      return (
        `<div class="bl-branch"><div class="cap">${esc(br.name)}</div>` +
        `<div class="bl-fl">${esc(br.flavour)}</div>` +
        list
          .map(
            (r) =>
              `<div class="bl-step ${r.state}">
                 <b>${esc(r.def.name)}</b>
                 <span>${esc(r.def.rules)}</span>
                 <i>${esc(r.def.flavour)}</i>
                 ${stateLine(r)}
               </div>`,
          )
          .join('') +
        `</div>`
      );
    }).join('');
    this.el.innerHTML = `
      <div class="wrap wide">
        <div class="title" style="font-size:30px">The Bloodline</div>
        <div class="tagline">What survives the Hearth going out.</div>
        <div class="bl-head">
          <div class="fact"><b>${bank.legacy}</b><span>Legacy held</span></div>
          <div class="fact"><b>${bank.earnedTotal}</b><span>Ever banked</span></div>
          <div class="fact"><b>${bank.runs}</b><span>Runs remembered</span></div>
          <div class="fact"><b>${bank.bestWave}</b><span>Deepest wave</span></div>
          <div class="bl-how">Legacy is paid at the end of every run, by depth: each wave survived is
          worth its own ten — one apiece to wave 10, two apiece to 20, and so on — scaled by the
          weight of the night. Dying at wave 22 on Ash banks about 36.</div>
        </div>
        <div class="bl-grid">${cols}</div>
        <div class="go" style="margin-top:22px"><span class="back" data-back>Back</span></div>
      </div>`;
    for (const el of this.el.querySelectorAll('[data-buy]')) {
      (el as HTMLElement).onclick = () => {
        const r = bloodline().buy((el as HTMLElement).dataset.buy as string);
        // A refused click on this screen can only be a race with itself —
        // the button is only drawn when the purchase is legal — but the
        // sentence is there if it ever happens.
        if (!r.ok && r.why) console.warn(r.why);
        this.renderBloodline();
      };
    }
    // The Bloodline and the Codex hang off the title screen, not off `Alone`.
    this.wireBack('title');
  }

  /** Which codex tab and entry are open. Fields, not markup — survive re-render. */
  private codexTab: CodexTab = 'soldiers';
  private codexId = '';

  /**
   * The Codex: the kingdom screen's rail-and-sheet furniture, pointed at the
   * whole catalogue. Everything printed is quoted from `src/data` through
   * `ui/codex.ts`, so this screen cannot disagree with the game.
   */
  private renderCodex(): void {
    if (!this.codexId) this.codexId = codexFirst(this.codexTab);
    const rows = codexRail(this.codexTab);
    if (!rows.some((r) => r.id === this.codexId)) this.codexId = codexFirst(this.codexTab);
    const tabs = CODEX_TABS.map(
      (t) =>
        `<div class="opt${t.id === this.codexTab ? ' on' : ''}" data-tab="${t.id}" style="min-width:110px;text-align:center">${esc(t.label)}</div>`,
    ).join('');
    this.el.innerHTML = `
      <div class="wrap wide">
        <div class="title" style="font-size:26px">The Codex</div>
        <div class="tagline">The same text the tooltips print, in one book.</div>
        <div class="row"><div class="opts">${tabs}</div></div>
        <div class="kingdom">
          <div class="rail" id="cx-rail"></div>
          <div class="sheet" id="cx-sheet">${codexSheet(this.codexTab, this.codexId)}</div>
        </div>
        <div class="foot"><span class="back" data-back>Back</span></div>
      </div>`;
    const rail = this.el.querySelector('#cx-rail') as HTMLElement;
    let group = '';
    for (const r of rows) {
      if (r.group !== group) {
        group = r.group;
        const h = document.createElement('div');
        h.className = 'kin';
        h.textContent = group;
        rail.appendChild(h);
      }
      const row = document.createElement('div');
      row.className = 'kd' + (r.id === this.codexId ? ' on' : '');
      row.innerHTML = `<div class="banner" style="background:rgba(200,164,76,0.35)"></div><div><div class="nm">${esc(r.label)}</div><div class="of">${esc(r.sub)}</div></div>`;
      row.onclick = () => {
        this.codexId = r.id;
        // Swap the sheet and the highlight without rebuilding the rail, so
        // the scroll position survives the click — the market form's lesson.
        (this.el.querySelector('#cx-sheet') as HTMLElement).innerHTML = codexSheet(this.codexTab, this.codexId);
        for (const el of rail.querySelectorAll('.kd.on')) el.classList.remove('on');
        row.classList.add('on');
      };
      rail.appendChild(row);
    }
    const on = rail.querySelector('.kd.on') as HTMLElement | null;
    if (on) on.scrollIntoView({ block: 'center', behavior: 'auto' });
    for (const el of this.el.querySelectorAll('[data-tab]')) {
      (el as HTMLElement).onclick = () => {
        this.codexTab = (el as HTMLElement).dataset.tab as CodexTab;
        this.codexId = '';
        this.renderCodex();
      };
    }
    this.wireBack('title');
  }

  private steps(at: 1 | 2): string {
    const one = at === 1 ? '<i class="on">1 · The Realm</i>' : '<i class="done" data-step="1">1 · The Realm</i>';
    const two = at === 2 ? '<i class="on">2 · The Kingdom</i>' : '<i><s>2 · The Kingdom</s></i>';
    return `<div class="steps">${one}<span>—</span>${two}</div>`;
  }

  /**
   * Step one: the board.
   *
   * **This is deliberately not a map browser**, and the reason is written on the
   * screen as well as here. `worldgen` is rewriting world generation and the
   * board's shape will change; a browser built against today's realm would be a
   * browser of one thing that then had to be thrown away. What is here instead
   * is the realm as it actually stands, counted out of the same tables the
   * campaign reads, plus the difficulty of holding a corner of it — which is a
   * real decision and keeps the step from being a speed bump.
   *
   * What this step needs from `worldgen` to become the browser it should be is
   * printed on the screen itself, not buried in a comment, because the person
   * who most needs to read it is the one looking at the screenshot.
   */
  private renderRealm(): void {
    const islands = PROVINCES.filter((p) => (p as { island?: boolean }).island).length;
    this.el.innerHTML = `
      <div class="wrap">
        <div class="title" style="font-size:30px">The Long Night</div>
        ${this.steps(1)}
        <div class="realm-card">
          <div>
            <h3>The Riven Sea</h3>
            <p>One inland sea with seven angular reaches around it, and channels between them. Most
            of the board has no road home: a navy is the price of entry to nearly all of it, and a
            sea lane is a border, so two crowns facing each other across the water are neighbours
            whether they like it or not.</p>
            <div class="facts">
              <div class="fact"><b>${PROVINCE_COUNT}</b><span>Provinces</span></div>
              <div class="fact"><b>${NATIONS.length}</b><span>Crowns</span></div>
              <div class="fact"><b>${REGIONS.length}</b><span>Regions</span></div>
              <div class="fact"><b>${SEA_LANES.length}</b><span>Sea lanes</span></div>
              <div class="fact"><b>${islands}</b><span>No road home</span></div>
              <div class="fact"><b>1</b><span>Contested heartland</span></div>
            </div>
          </div>
          <div>
            <div class="seedline">seed ${seedToCode(this.seed)} · <span class="back" data-reseed>reroll</span></div>
          </div>
        </div>
        <div class="seam">
          <b>One realm, for now</b>
          The coastline is grown from a fixed seed, so every campaign is played on the same board.
          When world generation can hand this screen a realm by name, this step becomes a chart with
          the crowns marked on it, and the kingdom you pick next is one you pick <i>off the map</i>.
        </div>
        <div class="go" style="margin-top:22px">
          <span class="seal" id="next">Choose your kingdom</span>
          <span class="back" data-back>Back</span>
        </div>
      </div>`;

    const rs = this.el.querySelector('[data-reseed]') as HTMLElement | null;
    if (rs) {
      rs.onclick = () => {
        this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
        this.renderRealm();
      };
    }
    (this.el.querySelector('#next') as HTMLElement).onclick = () => this.render('kingdom');
    this.wireBack();
  }

  /**
   * Step two: the kingdom, and everything that follows from it.
   *
   * The layout is decided by one finding from the art work and nothing else: at
   * thumbnail size only four things read — the crest/ear/crown outline,
   * `frameScale`, the value of the skin against the niche, and the accent
   * colour. Skull shape, brow, eyes and tusks do not appear until 104px and up.
   *
   * So this is **not** a grid of small heads. The unselected kingdoms are
   * identified by silhouette, banner and name in a rail; the selected one gets a
   * portrait large enough to be a face. A grid of twenty-two thumbnails would
   * have been twenty-two grey ovals and the screen would have failed on sight.
   *
   * The rail carries one more thing, and it is the thing that makes the choice a
   * choice: every row states what stops that kingdom raising one more company.
   * Scanning the rail is therefore a comparison of eleven *games* rather than of
   * eleven faces, which is what the race layer was built to be able to say.
   */
  /**
   * The kingdom picker, doing triple duty.
   *
   * `coop` sends the chosen kingdom to the room instead of starting a run: it
   * is the crown this seat takes, and the server seats it on the one realm
   * everybody is playing. The host's choice also decides the board itself —
   * which seed, which difficulty — because a realm is generated once.
   */
  private renderKingdom(realm: boolean, coop = false): void {
    // Read by `wireSheet`, which owns the Start button a screen further down.
    this.kingdomForCoop = coop;
    const sheet = kingdomSheet(this.kingdomId);
    this.el.innerHTML = `
      <div class="wrap wide">
        <div class="title" style="font-size:26px">${realm ? 'The Long Night' : 'Hold the Hearth'}</div>
        ${realm ? this.steps(2) : '<div class="tagline">One kingdom, one settlement, and the dark outside it.</div>'}
        <div class="kingdom">
          <div class="rail" id="rail"></div>
          <div class="sheet" id="sheet">${this.sheetHtml(sheet)}</div>
        </div>
        <div class="foot">
          <span class="seal" id="start">${realm ? 'Take the crown' : 'Hold the line'}</span>
          <span class="back" data-back-step>Back</span>
          <span class="seedline">seed ${seedToCode(this.seed)}</span>
        </div>
      </div>`;

    const rail = this.el.querySelector('#rail') as HTMLElement;
    for (const g of kinOrder()) {
      const h = document.createElement('div');
      h.className = 'kin';
      h.textContent = g.label;
      rail.appendChild(h);
      for (const k of kingdomsOfKin(g.kin)) {
        const on = k.id === this.kingdomId;
        const row = document.createElement('div');
        row.className = 'kd' + (on ? ' on' : '');
        row.dataset.kingdom = k.id;
        row.innerHTML =
          `<div class="banner" style="background:${toHex(k.leader.look.accent)}"></div>` +
          leaderPortrait(k.race.face, k.leader.look, { size: THUMB, muted: !on }) +
          `<div><div class="nm">${esc(k.leader.title)} ${esc(k.leader.name)}</div>` +
          `<div class="of">${esc(k.race.name)} · ${esc(k.race.plainName.split('—')[0].trim())}</div>` +
          `<div class="bind">${esc(railLine(k.race.id))}</div></div>`;
        row.onclick = () => {
          this.kingdomId = k.id;
          this.renderKingdom(realm);
        };
        rail.appendChild(row);
      }
    }
    // Twenty-two rows do not fit, so the chosen kingdom has to be brought into
    // view or arriving on this screen with a goblin already selected shows a
    // rail of Men and a sheet about goblins. `'auto'` rather than `'smooth'`:
    // the shot harness photographs before an animation would have finished.
    const on = rail.querySelector('.kd.on') as HTMLElement | null;
    if (on) on.scrollIntoView({ block: 'center', behavior: 'auto' });
    this.wireSheet(realm);
    // Painted portraits, where ARTBRIEF.md's files exist. After the wiring so
    // a swapped face never carries a stale listener.
    hydrateLeaderArt(this.el);
  }

  /** The sheet is a string so it can be swapped without rebuilding the rail's listeners. */
  private sheetHtml(s: KingdomSheet): string {
    const l = s.kingdom.leader;
    const r = s.kingdom.race;
    const accent = toHex(l.look.accent);

    const chips = s.larder.grants.length + s.larder.denies.length;
    const larder =
      (chips
        ? `<div class="res">` +
          s.larder.grants.map((g) => `<span>${esc(g.label)}</span>`).join('') +
          s.larder.denies.map((d) => `<span class="no">${esc(d.label)}</span>`).join('') +
          `</div>`
        : // A bordered chip reading "nothing" looks like an empty input field.
          // The absence is a sentence, not a token.
          `<li style="color:var(--bone-deep)">The common list, and nothing else. No private currency, and nothing withheld.</li>`) +
      `<li>${
        s.larder.reapsSouls
          ? '<b>Souls are reaped.</b><i>Every body that falls on your ground yields them. Almost nobody else’s do.</i>'
          : '<b>Souls are bought.</b><i>Two doors only — the Soul Furnace, which is a building and a decision, and the market, which is a price.</i>'
      }</li>`;

    const numbers = s.mods.length
      ? s.mods
          .slice(0, 11)
          .map(
            (m) =>
              `<div class="num"><span>${esc(m.label)}</span><b class="${m.good ? 'up' : 'dn'}">${esc(m.value)}</b></div>`,
          )
          .join('')
      : `<li style="color:var(--bone-deep)">Nothing off the baseline. Everything this house is, it is in shape rather than in numbers.</li>`;

    // The Academy column is captioned rather than listed like a building,
    // because several races name their branch after one of their own buildings
    // — the Litany's Ossuary is both — and two identical headings in one list
    // read as a duplicate rather than as two different things.
    const theirs =
      s.buildings.map((b) => `<li><b>${esc(b.name)}</b><i>${esc(b.flavour)}</i></li>`).join('') +
      `<li><b style="color:var(--gold-deep)">Academy column · </b><b>${esc(s.academy.name)}</b>` +
      `<i>${esc(s.academy.flavour)} ${s.academy.nodes} nodes nobody else may learn.</i></li>` +
      (s.ownUnits.length
        ? `<li><b>${s.ownUnits.length} units of their own</b></li>`
        : `<li style="color:var(--bone-deep)"><b>No units of their own yet.</b><i>They field ${esc(faction(s.bannerFactionId).name)}’s roster — ${s.rosterSize} companies — under their own banner.</i></li>`);

    const layer = (cap: string, sub: string, ps: ReadonlyArray<{ name: string; rules: string }>): string =>
      `<div class="layer"><div class="cap">${esc(cap)}</div><div class="capsub">${esc(sub)}</div>` +
      ps.map((p) => `<div class="pv"><b>${esc(p.name)}</b><span>${esc(p.rules)}</span></div>`).join('') +
      `</div>`;

    const rivals = siblingHouses(r.id, l.id);
    const rival = rivals.length
      ? `<div class="rival"><b>The other kingdom of ${esc(r.name)}.</b> ${rivals
          .map(
            (o) =>
              `<u data-pick="${o.id}">${esc(o.title)} ${esc(o.name)}</u> — ${esc(o.epithet)}. The same bone to the ` +
              `millimetre, and no shared read: ${housesDiffer(l.look, o.look)} of the five identity levers differ, ` +
              `and three is the floor. Two kingdoms of one people, and they do not get on.`,
          )
          .join(' ')}</div>`
      : '';

    return (
      `<div class="head">
        <div>${leaderPortrait(r.face, l.look, { size: HERO_SHOT })}</div>
        <div>
          <h2>${esc(r.name)}</h2>
          <div class="who" style="color:${accent}">${esc(l.title)} ${esc(l.name)}</div>
          <div class="ep">${esc(l.epithet)}</div>
          <div class="meta">
            <span class="tagchip">${esc(s.kinLabel)}</span>
            <span class="tagchip">${esc(r.plainName)}</span>
            <span class="tagchip">Homeland — ${esc(s.homeland)}</span>
            <span class="tagchip">Fights as the ${esc(s.archetype.name)}</span>
          </div>
          <div class="fl">${esc(l.flavour)}</div>
        </div>
      </div>` +
      `<div class="band">
        <div class="cap">What stops you raising one more company</div>
        <h4>${esc(s.binding.name)}</h4>
        <p>${esc(s.binding.text)}</p>
        ${
          s.binding.enforced
            ? ''
            : `<div class="unbuilt">Not yet enforced — this people currently musters on beds and wages like everybody else.
               The rule above is written down and the system that will carry it is named in the source.</div>`
        }
      </div>` +
      `<div class="cols">
        <div class="col"><div class="cap">The larder</div>${larder}</div>
        <div class="col"><div class="cap">Theirs alone</div>${theirs}</div>
        <div class="col"><div class="cap">The numbers, folded</div>${numbers}</div>
      </div>` +
      `<div class="layers">
        ${layer('The people', `Every kingdom of ${r.name} has these.`, r.passives)}
        ${layer('This house', `${l.title} ${l.name} alone, on top of the people’s — never instead.`, l.passives)}
      </div>` +
      (s.archetype.passive
        ? `<div class="rival"><b>The character.</b> ${esc(l.name)} levels as the ${esc(s.archetype.name)} — ${esc(s.archetype.epithet)}. ${esc(s.archetype.passive)}</div>`
        : '') +
      rival
    );
  }

  private wireSheet(realm: boolean): void {
    for (const el of this.el.querySelectorAll('[data-pick]')) {
      (el as HTMLElement).onclick = () => {
        this.kingdomId = (el as HTMLElement).dataset.pick as string;
        this.renderKingdom(realm, this.kingdomForCoop);
      };
    }
    const back = this.el.querySelector('[data-back-step]') as HTMLElement | null;
    if (back) back.onclick = () => this.render(this.kingdomForCoop ? 'coop' : realm ? 'realm' : 'newgame');
    // The room's own start button says what it does: you are claiming a crown,
    // not starting a run — the run starts when every seat has one.
    const startEl = this.el.querySelector('#start') as HTMLElement | null;
    if (startEl && this.kingdomForCoop) startEl.textContent = 'Take this crown';
    for (const el of this.el.querySelectorAll('[data-step]')) {
      (el as HTMLElement).onclick = () => this.render('realm');
    }
    (this.el.querySelector('#start') as HTMLElement).onclick = () => {
      const k = kingdom(this.kingdomId);
      // The character is a *consequence* of the kingdom, so the hero is read off
      // the leader rather than asked for. `Cmd.PickHero` still does the work and
      // the hero system is inherited whole.
      const choice: CampaignChoice = {
        factionId: k.race.factionId,
        difficultyId: this.difficultyId,
        heroId: k.leader.archetype,
        seed: this.seed,
        raceId: k.race.id,
        kingdomId: k.id,
      };
      if (this.kingdomForCoop) {
        this.cb.onCoop({
          ...this.coopDraft,
          server: relayUrl(),
          host: !this.coopJoining,
          resume: this.coopResume,
          choice,
        });
      } else if (realm) {
        this.cb.onCampaign(choice);
      } else {
        this.cb.onSiegeOnly(choice);
      }
      this.hide();
    };
  }


  /**
   * Sound, and the keyboard.
   *
   * The in-game panel already owns these three sliders; this is the same
   * three before a run exists, because a player who wants the music down
   * wants it down *before* the first horn rather than after it.
   *
   * The bindings underneath them are `KEY_HELP` — the one list, the same one
   * the HUD's key sheet prints. The title screen used to carry a second copy
   * of it, typed out by hand, which is precisely the drift `ui/keys.ts` exists
   * to prevent and which its own header is the story of. There is one list
   * again, and this screen is where a player looks for it.
   */
  private renderSettings(): void {
    const a = this.cb.audioSettings?.() ?? { master: 0.55, effects: 1, ambience: 0.8, muted: false };
    const keys = KEY_HELP.map(
      (g) =>
        `<div><div class="kg">${esc(g.group)}</div>` +
        g.rows
          .map(([k, what]) => `<div class="krow"><span class="kk">${esc(k)}</span><span>${esc(what)}</span></div>`)
          .join('') +
        `</div>`,
    ).join('');
    this.el.innerHTML = `
      <div class="wrap">
        <div class="title" style="font-size:30px">Options</div>
        <div class="tagline">How loud the dark is, and every key it listens to.</div>
        <div class="row"><div class="lbl">Sound</div>
          <div class="opts"><div class="opt${a.muted ? '' : ' on'}" id="snd-on">On</div>
            <div class="opt${a.muted ? ' on' : ''}" id="snd-off">Muted</div></div></div>
        <div class="row"><div class="lbl">Master</div>
          <input type="range" class="co-in" id="v-master" min="0" max="1" step="0.05" value="${a.master}" /></div>
        <div class="row"><div class="lbl">Effects</div>
          <input type="range" class="co-in" id="v-effects" min="0" max="1" step="0.05" value="${a.effects}" /></div>
        <div class="row"><div class="lbl">Ambience</div>
          <input type="range" class="co-in" id="v-ambience" min="0" max="1" step="0.05" value="${a.ambience}" /></div>
        <div class="row"><div class="lbl">The keyboard</div>
          <div class="keysheet">${keys}</div>
          <div class="saveline">Every one of these is also a button. Nothing in this game needs a key.</div></div>
        <div class="go" style="margin-top:18px"><div class="seal ghost" data-back="1">BACK</div></div>
      </div>`;
    const set = (patch: Parameters<NonNullable<MenuCallbacks['onAudioSettings']>>[0]): void =>
      this.cb.onAudioSettings?.(patch);
    (this.el.querySelector('#snd-on') as HTMLElement).onclick = () => {
      set({ muted: false });
      this.render('settings');
    };
    (this.el.querySelector('#snd-off') as HTMLElement).onclick = () => {
      set({ muted: true });
      this.render('settings');
    };
    for (const key of ['master', 'effects', 'ambience'] as const) {
      const el = this.el.querySelector(`#v-${key}`) as HTMLInputElement;
      el.oninput = () => set({ [key]: Number(el.value) });
    }
    for (const el of this.el.querySelectorAll('[data-back]')) {
      (el as HTMLElement).onclick = () => this.render('title');
    }
  }

  /**
   * The co-op lobby: a relay address, a name to be known by, and the rooms
   * that relay currently holds.
   *
   * Hosting and joining are the same screen deliberately. The only real
   * difference is who carries the world recipe — the host goes on to pick a
   * kingdom, an ally goes straight into the room and inherits it — and making
   * that a consequence of which button you press keeps the screen honest
   * about how few decisions there actually are.
   */
  private renderCoop(): void {
    const saved = (k: string, d: string): string => {
      try {
        return localStorage.getItem(k) ?? d;
      } catch {
        return d;
      }
    };
    this.coopDraft.nickname ||= saved('hearth.coop.name', '');

    this.el.innerHTML = `
      <div class="wrap">
        <div class="title" style="font-size:30px">Hold It Together</div>
        <div class="tagline">One realm, a crown each, one clock between you.</div>
        <div class="row"><div class="lbl">Called</div>
          <input id="co-name" class="co-in" maxlength="18" placeholder="your name in the room"
            value="${esc(this.coopDraft.nickname)}" /></div>
        <div class="co-split">
          <div class="co-side">
            <h3>Open a room</h3>
            <input id="co-rn" class="co-in" maxlength="28" placeholder="room name — “the Ninth Bell”" />
            <div class="row" style="margin:10px 0 0"><div class="lbl">Seats</div>
              <div class="opts" id="co-seats"></div></div>
            <div class="go" style="margin-top:14px">
              <div class="seal" id="co-host">OPEN THE ROOM</div>
            </div>
            <p class="co-note">You pick your kingdom next; so does everyone who sits down. One
              realm, a crown each, and whatever you decide to do about each other. The run is kept
              on the server, so you can open the same room tomorrow and go on.</p>
          </div>
          <div class="co-side">
            <h3>Take a seat</h3>
            <div id="co-rooms" class="co-rooms">Looking…</div>
            <div class="go" style="margin-top:10px">
              <div class="seal ghost" id="co-refresh">LOOK AGAIN</div>
            </div>
          </div>
        </div>
        <div class="co-resume">
          <h3>Reopen a room</h3>
          <p class="co-note">Every room this hearth has held is still on it — the realm where it
            stopped, and one capital per crown. Reopen one and the people who played it are seated
            in their own kingdoms again the moment they sign in; the crowns are kept for them until
            they do. Any seat nobody comes back for, you can free from the lobby.</p>
          <div id="co-saves" class="co-rooms wide">Looking…</div>
        </div>
        <div class="co-err" id="co-err"></div>
        <div class="go" style="margin-top:6px"><div class="seal ghost" data-back="1">BACK</div></div>
      </div>`;

    const nameEl = this.el.querySelector('#co-name') as HTMLInputElement;
    const roomNameEl = this.el.querySelector('#co-rn') as HTMLInputElement;
    const errEl = this.el.querySelector('#co-err') as HTMLElement;
    const roomsEl = this.el.querySelector('#co-rooms') as HTMLElement;

    const readDraft = (): void => {
      this.coopDraft.nickname = nameEl.value.trim();
      this.coopDraft.roomName = roomNameEl.value.trim();
      try {
        localStorage.setItem('hearth.coop.name', this.coopDraft.nickname);
      } catch { /* private mode; the draft still works for this session */ }
    };
    const needName = (): boolean => {
      readDraft();
      if (this.coopDraft.nickname) return false;
      errEl.textContent = 'The room needs something to call you.';
      nameEl.focus();
      return true;
    };

    // Seats
    const seatsEl = this.el.querySelector('#co-seats') as HTMLElement;
    for (const n of [2, 3, 4]) {
      const b = document.createElement('div');
      b.className = `opt${this.coopDraft.players === n ? ' on' : ''}`;
      b.textContent = String(n);
      b.onclick = () => {
        this.coopDraft.players = n;
        for (const el of seatsEl.children) el.classList.remove('on');
        b.classList.add('on');
      };
      seatsEl.appendChild(b);
    }

    const paintRooms = (rooms: RoomInfo[]): void => {
      if (!rooms.length) {
        roomsEl.innerHTML = `<div class="co-empty">Nobody has a room open right now.</div>`;
        return;
      }
      roomsEl.innerHTML = rooms
        .map(
          (r) => `<div class="co-room${r.started || r.seats >= r.size ? ' full' : ''}"
              data-room="${esc(r.code)}">
            <b>${esc(r.name)}</b>
            <span>${esc(r.host)}’s room · ${r.seats}/${r.size} seated${
              r.started ? ' · under way' : ''
            }</span>
          </div>`,
        )
        .join('');
      for (const el of roomsEl.querySelectorAll('[data-room]')) {
        (el as HTMLElement).onclick = () => {
          if (el.classList.contains('full')) {
            errEl.textContent = 'That room is full or already under way.';
            return;
          }
          if (needName()) return;
          // Everyone picks their own crown — a room is several kingdoms on
          // one realm, not one kingdom shared out.
          this.coopDraft.room = (el as HTMLElement).dataset.room ?? '';
          this.coopJoining = true;
          this.coopResume = false;
          this.render('coop-kingdom');
        };
      }
    };

    /**
     * The saves this hearth could reopen — the host's side of the owner's third
     * item, and the one thing that turns "the server happens to keep files" into
     * a feature somebody can use.
     *
     * Each row names the crowns the room holds, because that is how a host
     * recognises their own room: not by a slug, but by *this is the one with
     * Ada's Ashen and Bran's Ironfang in it*. A room that is standing right now
     * says so and takes you to the same place — joining and reopening are one
     * door, and the difference is whether the realm is already up.
     */
    const savesEl = this.el.querySelector('#co-saves') as HTMLElement;
    const paintSaves = (saves: SavedRoom[]): void => {
      if (!saves.length) {
        savesEl.innerHTML = `<div class="co-empty">This hearth is holding nothing yet.</div>`;
        return;
      }
      savesEl.innerHTML = saves
        .map((sv) => {
          const crowns = sv.crowns.length
            ? sv.crowns
                .map((c) => {
                  let title = c.kingdomId;
                  try {
                    const k = kingdom(c.kingdomId);
                    title = `${k.race.name}`;
                  } catch {
                    /* a roster that has moved on since the save */
                  }
                  return `${esc(c.name)} — ${esc(title)}`;
                })
                .join(' · ')
            : 'no crowns taken yet';
          return `<div class="co-room" data-save="${esc(sv.code)}" data-size="${sv.size}"
              data-name="${esc(sv.name)}">
            <b>${esc(sv.name)}</b>
            <span>${sv.size} seats · ${esc(crowns)}${
              sv.open ? ' · open now' : sv.realm ? ' · filed' : ' · never begun'
            }</span>
          </div>`;
        })
        .join('');
      for (const el of savesEl.querySelectorAll('[data-save]')) {
        (el as HTMLElement).onclick = () => {
          if (needName()) return;
          const d = (el as HTMLElement).dataset;
          this.coopDraft.room = d.save ?? '';
          this.coopDraft.roomName = d.name ?? (d.save ?? '');
          this.coopDraft.players = Math.max(2, Math.min(4, Number(d.size ?? 2)));
          // Reopening is hosting: the seat that opens a room is the one that
          // may begin it, and a room with a save already knows its own recipe.
          this.coopJoining = false;
          this.coopResume = true;
          this.render('coop-kingdom');
        };
      }
    };

    const refresh = (): void => {
      readDraft();
      roomsEl.textContent = 'Looking…';
      savesEl.textContent = 'Looking…';
      errEl.textContent = '';
      listRooms(relayUrl())
        .then(paintRooms)
        .catch((e: Error) => {
          roomsEl.innerHTML = `<div class="co-empty">—</div>`;
          errEl.textContent = e.message;
        });
      listRoomSaves(relayUrl())
        .then(paintSaves)
        .catch(() => {
          savesEl.innerHTML = `<div class="co-empty">—</div>`;
        });
    };
    (this.el.querySelector('#co-refresh') as HTMLElement).onclick = refresh;
    refresh();

    (this.el.querySelector('#co-host') as HTMLElement).onclick = () => {
      if (needName()) return;
      if (!this.coopDraft.roomName) this.coopDraft.roomName = `${this.coopDraft.nickname}’s hearth`;
      // A room code that is stable for a given host and name, so re-opening
      // the same room tomorrow finds yesterday's save waiting on the relay.
      this.coopJoining = false;
      this.coopResume = false;
      this.coopDraft.room = this.coopDraft.roomName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 24) || 'hearth';
      this.render('coop-kingdom');
    };

    // The room is its own door off the title, so Back is the title.
    for (const el of this.el.querySelectorAll('[data-back]')) {
      (el as HTMLElement).onclick = () => this.render('title');
    }
  }

  private renderBattle(): void {
    this.el.innerHTML = `
      <div class="wrap">
        <div class="title" style="font-size:30px">Battles</div>
        <div class="tagline">Nothing to build. Nothing to save. Only the field.</div>
        <div class="row"><div class="lbl">Scenario</div><div class="opts" id="scn"></div></div>
        <div class="row"><div class="lbl">Your host</div><div class="opts" id="fac"></div></div>
        <div class="row"><div class="lbl">Their host</div><div class="opts" id="efac"></div></div>
        <div class="row"><div class="lbl">Sides</div><div class="opts" id="sides"></div></div>
        ${
          this.allyFactionId === null
            ? ''
            : `<div class="row"><div class="lbl">Your ally</div><div class="opts" id="afac"></div></div>
        <div class="row"><div class="lbl">Their ally</div><div class="opts" id="eafac"></div></div>`
        }
        <div class="row"><div class="lbl">Army size</div><div class="opts" id="pts"></div></div>
        <div class="brief" id="brief"></div>
        <div class="go">
          <span class="seal" id="start">Take the field</span>
          <span class="back" data-back>Back</span>
          <span class="seedline">seed ${seedToCode(this.seed)}</span>
        </div>
      </div>`;

    const scnEl = this.el.querySelector('#scn') as HTMLElement;
    for (const s of SCENARIOS) {
      const o = document.createElement('div');
      o.className = 'opt' + (s.id === this.scenario ? ' on' : '');
      o.innerHTML = `${s.name}<span class="sub">${s.flavour}</span>`;
      o.style.minWidth = '186px';
      o.onclick = () => {
        this.scenario = s.id;
        this.renderBattle();
      };
      scnEl.appendChild(o);
    }
    const facEl = this.el.querySelector('#fac') as HTMLElement;
    for (const f of PLAYABLE_FACTIONS) {
      const o = document.createElement('div');
      o.className = 'opt' + (f.id === this.factionId ? ' on' : '');
      o.innerHTML = `${esc(f.name)}<span class="sub">${esc(f.race)}</span>`;
      o.onclick = () => {
        this.factionId = f.id;
        this.renderBattle();
      };
      facEl.appendChild(o);
    }
    const efacEl = this.el.querySelector('#efac') as HTMLElement;
    for (const f of FACTIONS) {
      const o = document.createElement('div');
      o.className = 'opt' + (f.id === this.enemyFactionId ? ' on' : '');
      o.innerHTML = `${esc(f.name)}<span class="sub">${esc(f.race)}</span>`;
      o.onclick = () => {
        this.enemyFactionId = f.id;
        this.renderBattle();
      };
      efacEl.appendChild(o);
    }
    /**
     * One against one, or two against two.
     *
     * The pair is set and cleared together — see `BattleChoice`. The defaults
     * when it is switched on are the first playable crown that is not already
     * yours and the first invader that is not already theirs, so the toggle
     * always produces a legal field without the player having to choose twice.
     */
    const sidesEl = this.el.querySelector('#sides') as HTMLElement;
    for (const two of [false, true]) {
      const o = document.createElement('div');
      o.className = 'opt' + ((this.allyFactionId !== null) === two ? ' on' : '');
      o.innerHTML = two
        ? `Two against two<span class="sub">a second banner a side</span>`
        : `One against one<span class="sub">your host and theirs</span>`;
      o.style.minWidth = '186px';
      o.onclick = () => {
        if (two) {
          this.allyFactionId =
            PLAYABLE_FACTIONS.find((f) => f.id !== this.factionId)?.id ?? this.factionId;
          this.enemyAllyFactionId =
            FACTIONS.find((f) => f.id !== this.enemyFactionId)?.id ?? this.enemyFactionId;
        } else {
          this.allyFactionId = null;
          this.enemyAllyFactionId = null;
        }
        this.renderBattle();
      };
      sidesEl.appendChild(o);
    }

    if (this.allyFactionId !== null) {
      const afacEl = this.el.querySelector('#afac') as HTMLElement;
      for (const f of PLAYABLE_FACTIONS) {
        const o = document.createElement('div');
        o.className = 'opt' + (f.id === this.allyFactionId ? ' on' : '');
        o.innerHTML = `${esc(f.name)}<span class="sub">${esc(f.race)}</span>`;
        o.onclick = () => {
          this.allyFactionId = f.id;
          this.renderBattle();
        };
        afacEl.appendChild(o);
      }
      const eafacEl = this.el.querySelector('#eafac') as HTMLElement;
      for (const f of FACTIONS) {
        const o = document.createElement('div');
        o.className = 'opt' + (f.id === this.enemyAllyFactionId ? ' on' : '');
        o.innerHTML = `${esc(f.name)}<span class="sub">${esc(f.race)}</span>`;
        o.onclick = () => {
          this.enemyAllyFactionId = f.id;
          this.renderBattle();
        };
        eafacEl.appendChild(o);
      }
    }

    const ptsEl = this.el.querySelector('#pts') as HTMLElement;
    for (const p of ARMY_SIZES) {
      const o = document.createElement('div');
      o.className = 'opt' + (p.points === this.points ? ' on' : '');
      o.innerHTML = `${p.label}<span class="sub">${p.note}</span>`;
      o.onclick = () => {
        this.points = p.points;
        this.renderBattle();
      };
      ptsEl.appendChild(o);
    }
    const s = SCENARIOS.find((x) => x.id === this.scenario) ?? SCENARIOS[0];
    (this.el.querySelector('#brief') as HTMLElement).innerHTML =
      `<b style="color:var(--gold)">Objective</b> — ${s.objective}<br>` +
      `An army breaks when most of its companies are dead or running. ` +
      `Charges land hardest in the flank and the rear; missile troops run dry; ` +
      `everyone tires.`;

    (this.el.querySelector('#start') as HTMLElement).onclick = () => {
      this.cb.onBattle({
        scenario: this.scenario,
        factionId: this.factionId,
        enemyFactionId: this.enemyFactionId,
        points: this.points,
        difficultyId: this.difficultyId,
        seed: this.seed,
        allyFactionId: this.allyFactionId,
        enemyAllyFactionId: this.enemyAllyFactionId,
      });
      this.hide();
    };
    this.wireBack();
  }

  /**
   * Back from a mode's own screen goes up one level, not all the way home —
   * and one level up from a mode is now the list of modes, since there is no
   * alone-or-together screen between them any more.
   */
  private wireBack(to: Screen = 'newgame'): void {
    const b = this.el.querySelector('[data-back]') as HTMLElement | null;
    if (b) b.onclick = () => this.render(to);
  }
}
